import { ColorLabel } from 'ui/components/elements/ColorLabel/ColorLabel';
import { IAreaOption } from 'domain/entities/Buyer/Buyer';
import { MATCH_STATUSES } from 'domain/entities/Match/Match';
import dayjs from 'dayjs';
import PointIcon from 'ui/images/point.svg';
import BsPeopleIcon from 'ui/images/bs_people.svg';
import BsGraphUpIcon from 'ui/images/bs_graph_up.svg';
import styled from '@emotion/styled';
import { mqMin } from 'utils/responsive';
import { SubTitleLabel } from 'ui/components/elements/ColorLabel/SubTitleLabel';

const Wrapper = styled.div<{ isPreview?: boolean }>`
    position: relative;
    width: 100%;
    margin: ${(props) => (props.isPreview ? '0 auto 20px 0' : '0 auto 100px auto')};
    height: auto;
    background-color: var(--white);
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
    border-radius: 10px;
    min-width: 320px;
    min-height: 330px;

    ${mqMin('NARROW')} {
        max-width: 800px;
    }
`;

const ContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 30px;
    padding: 30px;
`;

const SectionContainer = styled.div`
    background: linear-gradient(135deg, #f8f9fd 0%, #ffffff 100%);
    border-radius: 12px;
    padding: 25px;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    border: 1px solid #e8eaed;
    white-space: pre-line;
    word-break: break-all;
`;

const SectionTitle = styled.div`
    font-size: var(--font-x-large);
    font-weight: var(--font-bold);
    color: var(--dark-blue);
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 2px solid var(--main-color-blue);
    position: relative;

    &::before {
        content: '';
        position: absolute;
        left: 0;
        bottom: -2px;
        width: 30px;
        height: 2px;
        background-color: var(--main-color-blue);
    }
`;

const InfoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 15px;
    margin-bottom: 15px;
`;

const InfoItem = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 0;
`;

const InfoLabel = styled.div`
    font-size: var(--font-medium);
    font-weight: var(--font-bold);
    color: #666;
    min-width: 120px;
`;

const InfoValue = styled.div`
    font-size: var(--font-medium);
    font-weight: var(--font-bold);
    color: var(--dark-blue);
`;

const QuestionTitle = styled.div`
    font-size: var(--font-large);
    font-weight: var(--font-bold);
    margin-top: 10px;
    margin-left: 5px;
    padding-left: 25px;
    position: relative;
    color: var(--dark-blue);

    &::before {
        content: '';
        position: absolute;
        left: 0px;
        top: 50%;
        width: 18px;
        height: 18px;
        background-color: var(--dark-blue);
        border-radius: 50%;
        transform: translateY(-50%);
    }
`;

const Item = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
`;

const Title = styled.div`
    font-size: var(--font-large);
    font-weight: var(--font-bold);
    color: var(--dark-blue);
    display: flex;
    align-items: center;
    margin-bottom: 10px;
    gap: 5px;
    img {
        width: 20px;
    }
`;

const TextArea = styled.div`
    font-size: var(--font-large);
    padding: 5px;
    border-radius: 10px;
    line-height: 1.5;

    p {
        line-height: 1.5;
        margin: 0;
    }
`;

const TextItemContainer = styled.div`
    gap: 5px;
    display: flex;
    flex-direction: column;
`;

const CreatedAt = styled.div`
    font-size: var(--font-medium);
    font-weight: var(--font-bold);
    color: var(--dark-blue);
    margin-left: auto;
    margin-right: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const GrayLabel = styled.div`
    border-radius: 4px;
    background-color: #f3f3f3;
    align-items: center;
    font-size: var(--font-medium);
    color: #222222;
    font-weight: 500;
    padding: 0.2rem 0.9rem;
`;

const IndustryDetailBalloon = styled.div`
    font-size: var(--font-large);
    margin-top: 5px;
    background-color: var(--soft-blue);
    padding: 15px;
    border-radius: 10px;
    position: relative;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06);
    line-height: 1.5;
    white-space: pre-wrap;

    &::before {
        content: '';
        position: absolute;
        top: -12px;
        left: 20px;
        width: 0;
        height: 0;
        border-left: 12px solid transparent;
        border-right: 12px solid transparent;
        border-bottom: 12px solid var(--soft-blue);
    }
`;

const NormalLabel = styled.div`
    font-size: var(--font-large);
    font-weight: var(--font-bold);
    color: var(--dark-blue);
    margin-top: 5px;
`;

const AmountContainer = styled.div`
    font-size: var(--font-large);
    font-weight: var(--font-bold);
    color: var(--dark-blue);
    margin-top: 5px;
    margin-bottom: 20px;
    gap: 5px;
    display: flex;
    flex-direction: column;

    h2 {
        font-size: var(--font-xxx-large);
        font-weight: 800;
    }
`;

const AmountRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
`;

const AreaLabelContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    align-items: center;
`;

const ItemWrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    border-bottom: 1px solid #e0e0e0;
    padding: 6px 0 18px 0;
    margin: 5px 0 10px 0;
`;

const BuyerLabel = styled.div`
    border-radius: 8px;
    padding: 0.5rem 0.825rem;
    font-weight: 600;
    font-size: var(--font-large);
    line-height: 1;
    letter-spacing: 0.025em;
    white-space: nowrap;
    width: fit-content;
    color: white;
    background: var(--main-color-blue);
    display: flex;
    align-items: center;
    justify-content: center;
`;

const HorizontalContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const StatusBadge = styled.div`
    border-radius: 30px;
    padding: 0.5rem 0.825rem;
    font-weight: 700;
    line-height: 14.4px;
    letter-spacing: 0%;
    white-space: nowrap;
    margin-right: auto;
    color: white;
    background-color: #ee793f;
    font-size: 12px;
    font-size: var(--font-medium);
`;

const SubtitleContainer = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
    padding: 20px 10px 0 30px;
    margin-bottom: -10px;
`;

const Subtitle = styled.div`
    font-size: var(--font-x-large);
    font-weight: var(--font-bold);

    span {
        font-size: var(--font-medium);
        color: #696969;
    }
`;

interface PublicBuyerDetailProps {
    buyer: any;
    areaChoices: IAreaOption[];
    employeeChoices: string[][];
    revenueChoices: string[][];
    industryChoices: string[][];
    matchStatus?: string;
    isPreview?: boolean;
}

export const PublicBuyerDetail: React.FC<PublicBuyerDetailProps> = ({
    buyer,
    areaChoices,
    employeeChoices,
    revenueChoices,
    industryChoices,
    matchStatus,
    isPreview = false,
}) => {
    const getLabel = (choices: string[][], key: string) => {
        if (!choices || choices.length === 0) return key;
        const match = choices.find(([value]) => value === key);
        return match ? match[1] : key;
    };

    const getAreaName = (areaCode: string, areaChoices: IAreaOption[]) => {
        if (!areaChoices || areaChoices.length === 0) return areaCode;
        for (const areaGroup of areaChoices) {
            const child = areaGroup.children.find((child) => child.code === areaCode);
            if (child) {
                return child.name;
            }
        }
        return areaCode;
    };

    return (
        <Wrapper isPreview={isPreview}>
            {isPreview && (
                <SubtitleContainer>
                    <Subtitle>
                        プレビュー<span>（マッチング前に公開する画面）</span>
                    </Subtitle>
                </SubtitleContainer>
            )}
            <ContentWrapper>
                {/* 買収希望条件 */}
                <SectionContainer>
                    <SectionTitle>
                        <HorizontalContainer>
                            <Item>{buyer?.label && <BuyerLabel>{buyer.label}</BuyerLabel>}</Item>
                            {matchStatus && (
                                <StatusBadge>
                                    {matchStatus === MATCH_STATUSES.MATCH_STATUS_WAITING_ACCEPT && 'オファー済み'}
                                    {matchStatus === MATCH_STATUSES.MATCH_STATUS_CONNECTED && 'マッチング済み'}
                                    {matchStatus === MATCH_STATUSES.MATCH_STATUS_EXPIRED && 'オファー期限切れ'}
                                    {matchStatus === MATCH_STATUSES.MATCH_STATUS_DECLINED && 'オファー辞退'}
                                </StatusBadge>
                            )}
                            <CreatedAt>登録日：{dayjs(buyer.created_at).format('YYYY/MM/DD')}</CreatedAt>
                        </HorizontalContainer>
                    </SectionTitle>
                    {buyer.is_sole_proprietor && (
                        <div style={{ marginBottom: '10px' }}>
                            <ColorLabel
                                color="#000"
                                backgroundColor="var(--cream)"
                                borderColor="#000"
                                fontWeight="normal"
                                borderRadius="14px"
                            >
                                個人事業主
                            </ColorLabel>
                        </div>
                    )}
                    {buyer.target_company_purchase_amount ? (
                        <AmountContainer>
                            <span>買収希望案件金額</span>
                            <AmountRow>
                                <h2>{buyer.target_company_purchase_amount}</h2>
                                {buyer.has_acquisition_experience === '1' && (
                                    <ColorLabel
                                        color="#008000"
                                        backgroundColor="#d3ffa8"
                                        fontSize="var(--font-large)"
                                        borderColor="#008000"
                                        padding="0.3rem 0.8rem"
                                    >
                                        買収実績1件あり！
                                    </ColorLabel>
                                )}
                                {buyer.has_acquisition_experience === '2' && (
                                    <ColorLabel
                                        color="#EA5419"
                                        backgroundColor="#FFEBD8"
                                        fontSize="var(--font-large)"
                                        borderColor="#EA5419"
                                        borderWidth="2px"
                                        padding="0.3rem 0.8rem"
                                    >
                                        買収実績2件以上！
                                    </ColorLabel>
                                )}
                            </AmountRow>
                        </AmountContainer>
                    ) : (
                        <>
                            {buyer.has_acquisition_experience === '1' && (
                                <div style={{ marginBottom: '10px' }}>
                                    <ColorLabel
                                        color="#008000"
                                        backgroundColor="#d3ffa8"
                                        fontSize="var(--font-large)"
                                        borderColor="#008000"
                                        borderWidth="2px"
                                        padding="0.3rem 0.8rem"
                                    >
                                        買収実績1件あり！
                                    </ColorLabel>
                                </div>
                            )}
                            {buyer.has_acquisition_experience === '2' && (
                                <div style={{ marginBottom: '10px' }}>
                                    <ColorLabel
                                        color="#EA5419"
                                        backgroundColor="#FFEBD8"
                                        fontSize="var(--font-large)"
                                        borderColor="#EA5419"
                                        borderWidth="2px"
                                        padding="0.3rem 0.8rem"
                                    >
                                        買収実績2件以上！
                                    </ColorLabel>
                                </div>
                            )}
                        </>
                    )}

                    {buyer.target_company_employee_size && (
                        <ItemWrap>
                            <Title>
                                <img src={BsGraphUpIcon} alt="買収希望案件企業の従業員数" />
                                買収希望案件企業の従業員数
                            </Title>
                            <NormalLabel>{buyer.target_company_employee_size}</NormalLabel>
                        </ItemWrap>
                    )}
                    {buyer.target_company_revenue_scale && (
                        <ItemWrap>
                            <Title>
                                <img src={BsGraphUpIcon} alt="買収希望案件の売上高" />
                                買収希望案件の売上高
                            </Title>
                            <NormalLabel>{buyer.target_company_revenue_scale}</NormalLabel>
                        </ItemWrap>
                    )}
                    {buyer.target_company_profit_level && (
                        <ItemWrap>
                            <Title>
                                <img src={BsGraphUpIcon} alt="買収希望案件の営業利益" />
                                買収希望案件の営業利益
                            </Title>
                            <NormalLabel>{buyer.target_company_profit_level}</NormalLabel>
                        </ItemWrap>
                    )}
                    {buyer.target_company_net_assets_level && (
                        <ItemWrap>
                            <Title>
                                <img src={BsGraphUpIcon} alt="買収希望案件企業の純資産金額" />
                                買収希望案件企業の純資産金額
                            </Title>
                            <NormalLabel>{buyer.target_company_net_assets_level}</NormalLabel>
                        </ItemWrap>
                    )}
                    <ItemWrap>
                        <Title>
                            <img src={BsPeopleIcon} alt="買収希望案件業種" />
                            買収希望案件業種
                        </Title>
                        <AreaLabelContainer>
                            {buyer.target_company_industries && buyer.target_company_industries.length > 0 ? (
                                buyer.target_company_industries.map((industry: string) => (
                                    <GrayLabel key={industry}>{getLabel(industryChoices, industry)}</GrayLabel>
                                ))
                            ) : (
                                <GrayLabel>希望なし</GrayLabel>
                            )}
                        </AreaLabelContainer>
                        {buyer.target_company_industry_details && (
                            <IndustryDetailBalloon>{buyer.target_company_industry_details}</IndustryDetailBalloon>
                        )}
                    </ItemWrap>
                    <ItemWrap>
                        <Title>
                            <img src={PointIcon} alt="買収希望案件エリア" />
                            買収希望案件エリア
                        </Title>
                        <AreaLabelContainer>
                            {buyer.area_name && buyer.area_name.length > 0 ? (
                                buyer.area_name.map((area: string) => (
                                    <GrayLabel key={area}>{getAreaName(area, areaChoices)}</GrayLabel>
                                ))
                            ) : (
                                <GrayLabel>希望なし</GrayLabel>
                            )}
                        </AreaLabelContainer>
                    </ItemWrap>

                    {buyer.other_buying_points && (
                        <TextItemContainer>
                            <SubTitleLabel>その他希望事項等</SubTitleLabel>
                            <TextArea style={{ whiteSpace: 'pre-line' }}>{buyer.other_buying_points}</TextArea>
                        </TextItemContainer>
                    )}
                </SectionContainer>

                {/* 自社情報セクション */}
                <SectionContainer>
                    <SectionTitle>自社情報</SectionTitle>
                    <InfoGrid>
                        <InfoItem>
                            <InfoLabel>従業員規模</InfoLabel>
                            <InfoValue>
                                {buyer.number_of_employees
                                    ? getLabel(employeeChoices, buyer.number_of_employees)
                                    : '未設定'}
                            </InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>売上高</InfoLabel>
                            <InfoValue>
                                {buyer.revenue_scale ? getLabel(revenueChoices, buyer.revenue_scale) : '未設定'}
                            </InfoValue>
                        </InfoItem>
                    </InfoGrid>
                    {buyer.business_description && (
                        <TextItemContainer>
                            <SubTitleLabel>事業内容</SubTitleLabel>
                            <TextArea style={{ whiteSpace: 'pre-line' }}>{buyer.business_description}</TextArea>
                        </TextItemContainer>
                    )}
                </SectionContainer>

                {/* その他の情報セクション */}
                {(buyer.fee_structure ||
                    buyer.other_buyers ||
                    buyer.contract_status ||
                    buyer.process_flow ||
                    buyer.business_synergy ||
                    buyer.support_system ||
                    buyer.question) && (
                    <SectionContainer>
                        <TextItemContainer>
                            <SubTitleLabel>仲介会社に聞いてみたいこと</SubTitleLabel>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {buyer.fee_structure && (
                                    <>
                                        <QuestionTitle>M&Aに関する手数料水準について</QuestionTitle>
                                        {buyer.fee_structure_txt && (
                                            <TextArea style={{ whiteSpace: 'pre-line' }}>
                                                {buyer.fee_structure_txt}
                                            </TextArea>
                                        )}
                                    </>
                                )}
                                {buyer.other_buyers && (
                                    <>
                                        <QuestionTitle>他の買い手候補について</QuestionTitle>
                                        {buyer.other_buyers_txt && (
                                            <TextArea style={{ whiteSpace: 'pre-line' }}>
                                                {buyer.other_buyers_txt}
                                            </TextArea>
                                        )}
                                    </>
                                )}
                                {buyer.contract_status && (
                                    <>
                                        <QuestionTitle>提案案件の契約状況について</QuestionTitle>
                                        {buyer.contract_status_txt && (
                                            <TextArea style={{ whiteSpace: 'pre-line' }}>
                                                {buyer.contract_status_txt}
                                            </TextArea>
                                        )}
                                    </>
                                )}
                                {buyer.process_flow && (
                                    <>
                                        <QuestionTitle>具体的な流れについて</QuestionTitle>
                                        {buyer.process_flow_txt && (
                                            <TextArea style={{ whiteSpace: 'pre-line' }}>
                                                {buyer.process_flow_txt}
                                            </TextArea>
                                        )}
                                    </>
                                )}
                                {buyer.business_synergy && (
                                    <>
                                        <QuestionTitle>
                                            貴社の想定する事業シナジー、事業展望、スキーム等について
                                        </QuestionTitle>
                                        {buyer.business_synergy_txt && (
                                            <TextArea style={{ whiteSpace: 'pre-line' }}>
                                                {buyer.business_synergy_txt}
                                            </TextArea>
                                        )}
                                    </>
                                )}
                                {buyer.support_system && (
                                    <>
                                        <QuestionTitle>支援体制、サポート内容について</QuestionTitle>
                                        {buyer.support_system_txt && (
                                            <TextArea style={{ whiteSpace: 'pre-line' }}>
                                                {buyer.support_system_txt}
                                            </TextArea>
                                        )}
                                    </>
                                )}
                                {buyer.question && (
                                    <>
                                        <QuestionTitle>その他の質問や要望</QuestionTitle>
                                        <TextArea style={{ whiteSpace: 'pre-line' }}>{buyer.question}</TextArea>
                                    </>
                                )}
                            </div>
                        </TextItemContainer>
                    </SectionContainer>
                )}
            </ContentWrapper>
        </Wrapper>
    );
};
